import { createSelector } from 'reselect';

import { payment, store, user, whitelabel } from '../../core/state';
import { GetRestaurantProps } from '../../core/state/store/store.types.d';
import { changeUtils } from '../../utils';
import {
  CollectionOrder,
  CollectionRestaurantDetail,
  DeliveryOrder,
  DeliveryRestaurantDetail,
  DeliveryType,
  GatewayPaymentAccount,
  PickupLocationType,
  TableServiceCategory,
  TipsConfig,
} from '../Api';
import { checkoutConnectUtils } from '../component/Checkout/utils';
import { BLANK } from '../constants/Constants';
import {
  getBasketItemsBridge,
  getPickupLocationIdOrDefault,
  getPickupLocationTypeOrDefault,
  getRoundUpAmount,
  getSelectedPaymentOrDefault,
  getSelectedRequestedTimeOrDefault,
} from '../redux/basket/selectors';
import * as paymentSelectors from './paymentSelectors';

type BasketProps = {
  menuId: number;
};

export const getOrderCapacityFeatureFlag = createSelector(
  [(state: WebOrder.State) => whitelabel.selectors.getAppConfig(state)],
  (config) => (config ? config.Features.includes('order-capacity') : false),
);

export const getRequestFullNameFeatureFlag = createSelector(
  [(state: WebOrder.State) => whitelabel.selectors.getAppConfig(state)],
  (config) => (config ? config.Features.includes('request-full-name') : false),
);

export const getItemTotal = createSelector(
  [(state: WebOrder.State) => getBasketItemsBridge(state)],
  (basketItems) => {
    let total = 0;
    if (basketItems) {
      total = basketItems.reduce(
        (acc, item) =>
          acc +
          item.Item.Price +
          item.Options.map((o) => o.Price).reduce((oagg, o) => oagg + o, 0),
        0,
      );
    }

    return total;
  },
);

export const getDeliveryFeeTaxAmount = (
  state: WebOrder.State,
  props: GetRestaurantProps,
) => {
  const Restaurant = store.selectors.getRestaurantDetails(state, props);
  let total = 0;
  if (Restaurant && Restaurant.DeliveryFeeTaxAmount) {
    total = Restaurant.DeliveryFeeTaxAmount;
  }

  return total;
};

export const getItemTaxTotal = createSelector(
  [(state: WebOrder.State) => getBasketItemsBridge(state)],
  (basketItems) => {
    let total = 0;

    if (basketItems) {
      total = basketItems.reduce(
        (acc, item) =>
          acc +
          item.Item.TaxValue +
          item.Options.map((o) => o.TaxValue).reduce((oagg, o) => oagg + o, 0),
        0,
      );
    }

    return total;
  },
);

const getPaymentOptions = paymentSelectors.getAvailablePayments;

export const getPaymentOption = createSelector(
  [
    (state: WebOrder.State) => payment.selectors.getSelectedPayment(state),
    getPaymentOptions,
    (state: WebOrder.State, props: GetRestaurantProps) =>
      store.selectors.getRestaurantDetails(state, props),
  ],
  (selectedPayment, payments, restaurant) => {
    if (!payments || !payments.length || !restaurant) {
      return undefined;
    }

    if (selectedPayment) {
      const tryKeepSameRefObject = payments.find(
        (p) => p.PaymentAccountId === selectedPayment.PaymentAccountId,
      );
      return tryKeepSameRefObject || selectedPayment;
    }

    let defaultPayment = payments.find((p) => p.IsDefaultPaymentMethod);
    // just in case
    if (!defaultPayment && payments[0]) {
      [defaultPayment] = payments;
    }

    if (
      defaultPayment.PaymentAccountType === 'CashPaymentAccount' &&
      !restaurant.AcceptCashOrders
    ) {
      defaultPayment = payments.find(
        (p) => p.PaymentAccountType !== 'CashPaymentAccount',
      );
    }

    return defaultPayment;
  },
);

export const getPaymentFee = createSelector(
  [
    getPaymentOption,
    (state: WebOrder.State, props: GetRestaurantProps & BasketProps) =>
      store.selectors.getRestaurantDetails(state, props),
    getItemTotal,
    (state: WebOrder.State) => state.basket.pickupLocation,
  ],
  (
    selectedPaymentOption,
    restaurant,
    basketTotal,
    selectedPickupLocationType,
  ) => {
    let total = 0;
    let fee = 0;
    let fixedFee = 0;
    let percentFee = 0;

    if (selectedPaymentOption && restaurant && basketTotal) {
      const { Ideal, Bancontact } = GatewayPaymentAccount;
      const isCashFee = ['CashPaymentAccount', Ideal, Bancontact].includes(
        selectedPaymentOption.PaymentAccountType,
      );

      const isCollectionOrder = (
        r: CollectionRestaurantDetail | DeliveryRestaurantDetail,
      ): r is CollectionRestaurantDetail =>
        (r as CollectionRestaurantDetail).TableServiceCatagory !== undefined;

      if (isCollectionOrder(restaurant)) {
        const isTableService = [
          TableServiceCategory.Generic,
          TableServiceCategory.Table,
        ].includes(restaurant.TableServiceCatagory);

        const { PickupLocationTypes } = restaurant;
        let locationType = PickupLocationType.TakeOut;
        if (PickupLocationTypes.includes(selectedPickupLocationType)) {
          locationType = selectedPickupLocationType;
        } else if (!PickupLocationTypes.includes(PickupLocationType.TakeOut)) {
          [locationType] = PickupLocationTypes;
        }

        const isTakeOutSelected =
          restaurant.DeliveryType === 1 && locationType === 0;

        if (isCashFee) {
          if (isTableService && !isTakeOutSelected) {
            fixedFee =
              restaurant.LocationServiceFixedFeeCashOrdersChargedToCustomerIncludingVat;
            percentFee =
              restaurant.LocationServicePercentFeeCashOrdersChargedToCustomerIncludingVat;
          } else {
            fixedFee =
              restaurant.FixedFeeCashOrdersChargedToCustomerIncludingVat;
            percentFee =
              restaurant.PercentFeeCashOrdersChargedToCustomerIncludingVat;
          }
        } else if (isTableService && !isTakeOutSelected) {
          fixedFee =
            restaurant.LocationServiceFixedFeeCardOrdersChargedToCustomerIncludingVat;
          percentFee =
            restaurant.LocationServicePercentFeeCardOrdersChargedToCustomerIncludingVat;
        } else {
          fixedFee = restaurant.FixedFeeCardOrdersChargedToCustomerIncludingVat;
          percentFee =
            restaurant.PercentFeeCardOrdersChargedToCustomerIncludingVat;
        }
      } else if (isCashFee) {
        fixedFee = restaurant.FixedFeeCashOrdersChargedToCustomerIncludingVat;
        percentFee =
          restaurant.PercentFeeCashOrdersChargedToCustomerIncludingVat;
      } else {
        fixedFee = restaurant.FixedFeeCardOrdersChargedToCustomerIncludingVat;
        percentFee =
          restaurant.PercentFeeCardOrdersChargedToCustomerIncludingVat;
      }

      fee = fixedFee + (basketTotal * percentFee) / 100;
      total = Number.isNaN(fee) ? 0 : fee;
    }
    return total;
  },
);

export const getDeliveryFee = createSelector(
  [
    (state: WebOrder.State, props: GetRestaurantProps) =>
      store.selectors.getRestaurantDetails(state, props),
  ],
  (Restaurant) => {
    let total = 0;
    if (Restaurant) {
      if ('DeliveryFee' in Restaurant) {
        total = Restaurant.DeliveryFee;
      }
    }

    return total;
  },
);

export const getVoucher = (state: WebOrder.State) => state.basket.voucher;

export const getVoucherTotal = createSelector([getVoucher], (voucher) => {
  if (voucher && voucher.code && voucher.amount) {
    const { taxAmount } = voucher;
    return voucher.amount + taxAmount;
  }

  return 0;
});

export const getTaxType = createSelector(
  [
    (state: WebOrder.State, props: GetRestaurantProps) =>
      store.selectors.getRestaurantDetails(state, props),
  ],
  (Restaurant) => {
    let taxType = 0;
    if (Restaurant) {
      if ('TaxType' in Restaurant) {
        taxType = Restaurant.TaxType;
      }
    }

    return taxType;
  },
);

type TotalProps =
  | {
      menuId: number;
      vrId: number;
      deliveryType: 'collection';
      prId: number;
    }
  | {
      menuId: number;
      vrId: number;
      deliveryType: 'delivery';
      deliveryLocationId: number;
    };

export const getTotal = createSelector(
  [
    getDeliveryFeeTaxAmount,
    getTaxType,
    getItemTotal,
    getItemTaxTotal,
    getPaymentFee,
    getDeliveryFee,
  ],
  (
    deliveryFeeTaxAmount,
    taxType,
    itemTotal,
    itemTaxTotal,
    paymentFeeTotal,
    deliveryFeeTotal,
  ) => {
    const total =
      itemTotal +
      paymentFeeTotal +
      deliveryFeeTotal +
      deliveryFeeTaxAmount +
      (taxType === 1 ? itemTaxTotal : 0);

    return changeUtils.round(total);
  },
);

// #endregion

// #region TIPS_ENABLED

export const getAppTipsEnabled = (state: WebOrder.State) => {
  const config = whitelabel.selectors.getAppConfig(state);
  if (config) {
    return config.TipsEnabled;
  }
  return false;
};

export const getRestaurantTipsEnabled = (
  state: WebOrder.State,
  props: GetRestaurantProps,
) => {
  const config = store.selectors.getRestaurantDetails(state, props);
  if (config) {
    return config.TipsEnabled;
  }
  return false;
};

export const getTipEnabled = createSelector(
  [
    (state: WebOrder.State) => getAppTipsEnabled(state),
    (state: WebOrder.State, props: TotalProps) =>
      getRestaurantTipsEnabled(state, props),
  ],
  (appTipsEnabled, restaurantTipsEnabled) =>
    appTipsEnabled && restaurantTipsEnabled,
);

export const getUserDefaultTipPercentage = (state: WebOrder.State) => {
  const config = user.selectors.getUserConfig(state);
  if (config) {
    return config.DefaultTipPercentage;
  }
  return 0;
};

export const getSelectedTipOrDefault = (
  state: WebOrder.State,
  props: TotalProps,
) => {
  let tipsConfig: TipsConfig;

  const restaurant = store.selectors.getRestaurantDetails(state, props);

  if (restaurant) {
    tipsConfig = restaurant.TipsConfig;
  }

  if (tipsConfig) {
    const tip = checkoutConnectUtils.getDefaultTipSetting(
      tipsConfig,
      state?.basket,
    );
    // for new-comers: 0 (zero) is falsy so if we have any number in state use that else default
    if (typeof tip === 'number') {
      return tip;
    }
  }
  return getUserDefaultTipPercentage(state);
};

const getCanTipOnSelectedPayment = (state: WebOrder.State) => {
  const payOption = getSelectedPaymentOrDefault(state);

  if (payOption && payOption.SupportsSubmitOrderTips) {
    return true;
  }
  return false;
};

export const getTipTotal = createSelector(
  [
    getTipEnabled,
    (state: WebOrder.State) => getCanTipOnSelectedPayment(state),
    (state: WebOrder.State, props: TotalProps) =>
      getSelectedTipOrDefault(state, props),
    getTotal,
  ],
  (tipsEnabled, canTipOnSelectedPayment, selectedOrDefaultTip, total) => {
    if (!tipsEnabled || !canTipOnSelectedPayment) {
      return 0;
    }

    const tipsTotal = changeUtils.round(total * (selectedOrDefaultTip / 100));

    return tipsTotal;
  },
);

export const getTotalToPay = createSelector(
  [getTotal, getTipTotal, getVoucherTotal],
  (totalAmount, tipsTotal, voucherTotal) => {
    const total = changeUtils.round(totalAmount + tipsTotal + voucherTotal);

    return total;
  },
);

export type BasketError =
  | 'empty_basket'
  | 'missing_restaurant_details'
  | 'below_minimum_amount'
  | 'missing_pre_order_time';

export const getBasketValidationError = createSelector(
  [
    (state: WebOrder.State) => getBasketItemsBridge(state),
    (state: WebOrder.State, props: OrderPayloadProps) =>
      store.selectors.getRestaurantDetails(state, props),
    getItemTotal,
    (state: WebOrder.State) => getSelectedRequestedTimeOrDefault(state),
  ],
  (basketItems, restaurant, itemTotal, requestedTime): BasketError => {
    if (!basketItems || (basketItems && !basketItems.length)) {
      return 'empty_basket';
    }

    // don't work w cached data
    if (!restaurant || !restaurant.FromAPI) {
      return 'missing_restaurant_details';
    }

    if (restaurant.MinimumOrder > itemTotal) {
      return 'below_minimum_amount';
    }

    const preOrderEnabled =
      restaurant.AcceptPreOrders && restaurant.PreOrderOptions;
    const preOrderExplicit =
      preOrderEnabled && restaurant.PreOrderOptions.RequireExplicitSelect;
    if (
      preOrderEnabled &&
      preOrderExplicit &&
      (!requestedTime || requestedTime === BLANK) // backend supports requestedTime="" sometimes 😱
    ) {
      return 'missing_pre_order_time';
    }

    return undefined;
  },
);

export type OrderPayloadProps =
  | {
      menuId: number;
      menuVersionGuid: string;
      vrId: number;
      deliveryType: 'collection';
      prId: number;
    }
  | {
      menuId: number;
      vrId: number;
      menuVersionGuid: string;
      deliveryType: 'delivery';
      deliveryLocationId: number;
    };

const getOrderPayloadBase = createSelector(
  [
    (_state: WebOrder.State, props: OrderPayloadProps) => props.vrId,
    (_state: WebOrder.State, props: OrderPayloadProps) => props.menuId,
    (_state: WebOrder.State, props: OrderPayloadProps) => props.menuVersionGuid,
    (_state: WebOrder.State, props: OrderPayloadProps) => props.deliveryType,
    (state: WebOrder.State) => getBasketItemsBridge(state),
    (state: WebOrder.State) => getSelectedRequestedTimeOrDefault(state),
    (state: WebOrder.State, props: OrderPayloadProps) =>
      getTipTotal(state, props),
    (state: WebOrder.State, props: OrderPayloadProps) =>
      getPaymentOption(state, props),
    (state: WebOrder.State, props: OrderPayloadProps) =>
      store.selectors.getRestaurantSummary(state, props),
    (state: WebOrder.State) => state.flipdish.Config.DeviceId,
    (state: WebOrder.State) =>
      state.basket.isRoundUpEnabled ? getRoundUpAmount(state) : 0,
  ],
  (
    vrId,
    menuId,
    menuVersionGuid,
    deliveryType,
    basketItems,
    requestedTime,
    tipAmount,
    paymentOption,
    restaurant,
    deviceId,
    roundUpAmount,
  ) => {
    if (
      !vrId ||
      !menuId ||
      !menuVersionGuid ||
      !basketItems.length ||
      !restaurant
    ) {
      return undefined;
    }

    const orderBase = {
      VirtualRestaurantId: vrId,
      deviceId,
      OrderItemVms: basketItems.map((item) => ({
        MenuItemId: item.Item.MenuItemId,
        MenuItemOptionSetItemIds: item.Options.map(
          (option) => option.MenuItemOptionSetItemId,
        ),
      })),
      TipAmount: tipAmount + roundUpAmount,
      RequestedDeliveryOrPickupTimeUtc: requestedTime,
      MenuId: menuId,
      VersionGuid: menuVersionGuid,
      DeliveryType:
        deliveryType === 'collection'
          ? DeliveryType.Collection
          : DeliveryType.Delivery,
      PaymentAccountId:
        paymentOption && paymentOption.PaymentAccountId > 0
          ? paymentOption.PaymentAccountId
          : undefined,
      // needed 4 collection and delivery
      PhysicalRestaurantId: restaurant.PhysicalRestaurantId,
    };

    return orderBase;
  },
);

export type OrderPayload = ReturnType<typeof getOrderPayload>;

export const getOrderPayload = createSelector(
  [
    getOrderPayloadBase,
    (_state: WebOrder.State, props: OrderPayloadProps) =>
      props.deliveryType === 'collection'
        ? props.prId
        : props.deliveryLocationId,
    getBasketValidationError,
    (state: WebOrder.State) => getPickupLocationTypeOrDefault(state),
    (state: WebOrder.State) => getPickupLocationIdOrDefault(state),
    (state: WebOrder.State) => state.flipdish.Location.geo,
  ],
  (
    orderBase,
    prOrDlId,
    basketError,
    pickupLocationType,
    tableNumber,
    userLocation,
  ) => {
    if (basketError) {
      return basketError;
    }
    if (!orderBase) {
      return undefined;
    }

    let orderPayload: DeliveryOrder | CollectionOrder;
    switch (orderBase.DeliveryType) {
      case DeliveryType.Collection:
        orderPayload = {
          ...orderBase,
          DeliveryType: DeliveryType.Collection,
          PhysicalRestaurantId: prOrDlId,
          PickupLocationType: pickupLocationType,
        };
        if (typeof tableNumber === 'number') {
          orderPayload.PickupLocationId = tableNumber;
        }
        break;

      case DeliveryType.Delivery:
        orderPayload = {
          ...orderBase,
          DeliveryType: DeliveryType.Delivery,
          DeliveryLocationId: prOrDlId,
          UserLocation: userLocation,
        };
        break;

      // no default
    }

    return orderPayload;
  },
);
